
import Vue from "vue";
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import UserModule from '../store/modules/User';
import store from '../store';
import api from "../service/api";
import { BusinessEntity } from "../model/BusinessEntity";
import { validateIBAN } from "./iban";

const { $vToastify: notifier } = new Vue() as any;
const userModule = getModule(UserModule, store);

interface FinanceStuffState {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    receivePayments: boolean;
    publicationConsent: boolean;
    isSoleProprietorship: boolean;
    isCompany: boolean;
    manualTransfers: boolean;
    przelewy24: boolean;
    paypal: boolean;
    personalAddress: FinanceStuff['personalAddress'];
    companyData: FinanceStuff['companyData'];
    corporateData: FinanceStuff['corporateData'];
    bankData: FinanceStuff['bankData'];
}

@Component
export default class FinanceStuff extends Vue {
    receivePayments = false;
    publicationConsent = false;
    @Prop({ default: true })
    public showActivator!: boolean;
    public isSoleProprietorship = false;
    public isCompany = false;
    public dialogVisible = false;
    public manualTransfers = false;
    public przelewy24 = false;
    public paypal = false;
    public highlightPaymentReceiving = false;
    public highlightPublicationConsent = false;
    private highlightPaymentTimeout: number | null = null;
    private highlightPublicationTimeout: number | null = null;
    public exitRunBlink = false;
    private exitRunBlinkTimeout: number | null = null;
    firstName: string = '';
    lastName: string = '';
    phoneNumber: string = '';
    email: string = '';
    personalAddress = {
        street: '',
        city: '',
        postCode: '',
        country: '',
    };
    companyData = {
        name: '',
        taxNumber: '',
        regon: '',
        street: '',
        city: '',
        postCode: '',
        country: '',
    };
    corporateData = {
        name: '',
        street: '',
        city: '',
        postCode: '',
        deliveryStreet: '',
        deliveryCity: '',
        deliveryPostCode: '',
        krs: '',
        districtCourt: '',
        commercialDivision: '',
        taxNumber: '',
        regon: '',
        country: '',
    };
    bankData = {
        bankName: '',
        IBAN: '',
        chargeClientWithFee: false,
    };
    twoFactorCode = '';
    twoFactorCodeErrors: string[] = [];
    public isCooldownActive = false;
    public cooldownTime = 0;
    private cooldownInterval: number | null = null;
    public financeTwofaDigits = ['', '', '', '', '', ''];
    public twoFactorNow = Date.now();
    private twoFactorGraceTimer: number | null = null;
    private savedState: FinanceStuffState | null = null;

    formValid = true;

    get firstNameRules() {
        return [
            (v: string) => (!this.publicationConsent && !this.receivePayments) || !!v
        ];
    }

    get lastNameRules() {
        return [
            (v: string) => (!this.publicationConsent && !this.receivePayments) || !!v
        ];
    }

    get phoneRules() {
        return [
            (v: string) => (!this.publicationConsent && !this.receivePayments) || !!v || 'Numer telefonu jest wymagany',
            (v: string) => (!this.publicationConsent && !this.receivePayments && !v) || /^[0-9-]{6,15}$/.test((v || '').trim()) || 'Podaj poprawny numer telefonu',
        ];
    }

    emailRules = [
        (v: string) => !!v || 'Adres email jest wymagany',
        (v: string) => /^[\w-\.]+@([\w-]+\.)+[\w-]+$/.test((v || '').trim()) || 'Adres email jest niepoprawny',
    ];

    twoFactorCodeRules = [
        (v: string) => !!v || 'Kod 2FA jest wymagany',
        (v: string) => /^\d{6}$/.test((v || '').trim()) || 'Kod 2FA musi mieć 6 cyfr',
    ];

    get personalStreetRules() {
        return [
            (v: string) => ((this.isSoleProprietorship || this.isCompany) || (!this.receivePayments && !this.publicationConsent)) ? true : !!v || 'Ulica jest wymagana',
        ];
    }

    get personalCityRules() {
        return [
            (v: string) => ((this.isSoleProprietorship || this.isCompany) || (!this.receivePayments && !this.publicationConsent)) ? true : !!v || 'Miasto jest wymagane',
        ];
    }

    get personalPostCodeRules() {
        return [
            (v: string) => ((this.isSoleProprietorship || this.isCompany) || (!this.receivePayments && !this.publicationConsent)) ? true : !!v || 'Kod pocztowy jest wymagany',
            (v: string) => ((this.isSoleProprietorship || this.isCompany) || (!this.receivePayments && !this.publicationConsent)) ? true : this.isValidPostCode(v) || 'Użyj formatu 00-000',
        ];
    }

    get personalCountryRules() {
        return [
            (v: string) => ((this.isSoleProprietorship || this.isCompany) || (!this.receivePayments && !this.publicationConsent)) ? true : !!(v || '').trim() || 'Kraj jest wymagany',
        ];
    }

    get shouldShowPersonalAddressSection() {
        return !this.isSoleProprietorship && !this.isCompany && (this.receivePayments || this.publicationConsent);
    }

    get companyNameRules() {
        return [
            (v: string) => !this.isSoleProprietorship ? true : !!v || 'Nazwa firmy jest wymagana',
        ];
    }

    get companyNipRules() {
        return [
            (v: string) => !this.isSoleProprietorship ? true : !!v || 'NIP jest wymagany',
            (v: string) => !this.isSoleProprietorship ? true : this.isDigitsLength(v, 10) || 'NIP musi mieć 10 cyfr',
            (v: string) => !this.isSoleProprietorship ? true : !!this.validateNip(this.stripNonDigits(v)) || 'NIP jest niepoprawny',
        ];
    }

    get companyRegonRules() {
        return [
            (v: string) => !this.isSoleProprietorship || !v ? true : ([9, 14].includes(this.stripNonDigits(v).length) || 'REGON musi mieć 9 lub 14 cyfr'),
        ];
    }

    get companyStreetRules() {
        return [
            (v: string) => !this.isSoleProprietorship ? true : !!v || 'Ulica jest wymagana',
        ];
    }

    get companyCityRules() {
        return [
            (v: string) => !this.isSoleProprietorship ? true : !!v || 'Miasto jest wymagane',
        ];
    }

    get companyPostCodeRules() {
        return [
            (v: string) => !this.isSoleProprietorship ? true : !!v || 'Kod pocztowy jest wymagany',
            (v: string) => !this.isSoleProprietorship ? true : this.isValidPostCode(v) || 'Użyj formatu 00-000',
        ];
    }

    get companyCountryRules() {
        return [
            (v: string) => !this.isSoleProprietorship ? true : !!(v || '').trim() || 'Kraj jest wymagany',
        ];
    }
    get corporateNameRules() { return [(v: string) => !this.isCompany ? true : !!v || 'Nazwa spółki jest wymagana']; }
    get corporateStreetRules() { return [(v: string) => !this.isCompany ? true : !!v || 'Ulica jest wymagana']; }
    get corporateCityRules() { return [(v: string) => !this.isCompany ? true : !!v || 'Miasto jest wymagane']; }
    get corporatePostCodeRules() {
        return [
            (v: string) => !this.isCompany ? true : !!v || 'Kod pocztowy jest wymagany',
            (v: string) => !this.isCompany ? true : this.isValidPostCode(v) || 'Użyj formatu 00-000',
        ];
    }
    get corporateDeliveryStreetRules() { return [(v: string) => !this.isCompany ? true : !!v || 'Ulica jest wymagana']; }
    get corporateDeliveryCityRules() { return [(v: string) => !this.isCompany ? true : !!v || 'Miasto jest wymagane']; }
    get corporateDeliveryPostCodeRules() {
        return [
            (v: string) => !this.isCompany ? true : !!v || 'Kod pocztowy jest wymagany',
            (v: string) => !this.isCompany ? true : this.isValidPostCode(v) || 'Użyj formatu 00-000',
        ];
    }
    get corporateKrsRules() { return [(v: string) => !this.isCompany ? true : !!v || 'KRS jest wymagany']; }
    get corporateDistrictCourtRules() { return [(v: string) => !this.isCompany ? true : !!v || 'Sąd rejonowy jest wymagany']; }
    get corporateCommercialDivisionRules() { return [(v: string) => !this.isCompany ? true : !!v || 'Wydział gospodarczy jest wymagany']; }
    get corporateNipRules() {
        return [
            (v: string) => !this.isCompany ? true : !!v || 'NIP jest wymagany',
            (v: string) => !this.isCompany ? true : this.isDigitsLength(v, 10) || 'NIP musi mieć 10 cyfr',
            (v: string) => !this.isCompany ? true : !!this.validateNip(this.stripNonDigits(v)) || 'NIP jest niepoprawny',
        ];
    }
    get corporateRegonRules() {
        return [
            (v: string) => !this.isCompany ? true : !!v || 'REGON jest wymagany',
            (v: string) => !this.isCompany ? true : ([9, 14].includes(this.stripNonDigits(v).length) || 'REGON musi mieć 9 lub 14 cyfr'),
        ];
    }
    get corporateCountryRules() { return [(v: string) => !this.isCompany ? true : !!(v || '').trim() || 'Kraj jest wymagany']; }

    get bankNameRules() {
        return [
            (v: string) => (!this.receivePayments || !this.manualTransfers) ? true : !!v || 'Nazwa banku jest wymagana',
        ];
    }

    get ibanRules() {
        return [
            (v: string) => (!this.receivePayments || !this.manualTransfers) ? true : !!v || 'Numer konta jest wymagany',
            (v: string) => (!this.receivePayments || !this.manualTransfers) ? true : !!this.validateIban(this.normalizeIban(v)) || 'IBAN jest niepoprawny',
        ];
    }

    private stripNonDigits(value: string) {
        return (value || '').replace(/\D/g, '');
    }

    private isDigitsLength(value: string, length: number) {
        return this.stripNonDigits(value).length === length;
    }

    private isValidPostCode(value: string) {
        return /^\d{2}-\d{3}$/.test((value || '').trim());
    }

    private formatPostCode(value: string) {
        const digits = this.stripNonDigits(value).slice(0, 5);
        return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
    }

    private formatPhone(value: string) {
        return (this.stripNonDigits(value).slice(0, 12).match(/.{1,3}/g) || []).join('-').slice(0, 15);
    }

    private formatPhoneNumber(value: string) {
        this.phoneNumber = this.formatPhone(value);
    }

    private formatPersonalPostCode(value: string) {
        this.personalAddress.postCode = this.formatPostCode(value);
    }

    private formatCompanyPostCode(value: string) {
        this.companyData.postCode = this.formatPostCode(value);
    }

    private onCorporatePostCodeInput(value: string) {
        this.corporateData.postCode = this.formatPostCode(value);
    }

    private onCorporateDeliveryPostCodeInput(value: string) {
        this.corporateData.deliveryPostCode = this.formatPostCode(value);
    }

    private onSoleProprietorshipChange(value: boolean) {
        this.isSoleProprietorship = value;
        if (value) this.isCompany = false;
    }

    private onCompanyChange(value: boolean) {
        this.isCompany = value;
        if (value) this.isSoleProprietorship = false;
    }

    private normalizeIban(value: string) {
        return (value || '').replace(/\s+/g, '').toUpperCase();
    }

    private validateNip(nip: string): boolean | undefined {
        // Must be exactly 10 digits
        if (!/^\d{10}$/.test(nip)) {
            return false;
        }

        const digits = nip.split("").map(Number);
        const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];

        const checksum = weights.reduce(
            (sum, weight, i) => sum + weight * digits[i],
            0
        );

        const remainder = checksum % 11;

        // remainder of 10 means the NIP is invalid
        if (remainder === 10) {
            return false;
        }

        return remainder === digits[9];
    }

    private validateIban(iban: string): boolean | undefined {
        return validateIBAN(iban);
    }

    @Watch('publicationConsent')
    @Watch('receivePayments')
    revalidate() {
        if (this.receivePayments) {
            this.ensureAtLeastOnePaymentMethodEnabled();
        }
        // Force re-evaluation of all validation rules
        (this.$refs.financeForm as any)?.validate();
    }

    @Watch('isSoleProprietorship')
    @Watch('isCompany')
    onCompanyTypeChanged() {
        this.$nextTick(() => {
            // Validate immediately after company fields are shown/hidden
            (this.$refs.financeForm as any)?.validate();
        });
    }

    async mounted() {
        // Ensure at least one payment method is enabled if payment receiving is already enabled
        if (this.receivePayments) {
            this.ensureAtLeastOnePaymentMethodEnabled();
        }
        // Tylko egzemplarz bez przycisku (showActivator=false) nasłuchuje eventów - unika podwójnego otwierania modala
        if (!this.showActivator) {
            this.$root.$on('openFinanceStuff', this.onOpenFinanceStuff);
            this.$root.$on('openFinanceStuffPublicationConsent', this.onOpenFinanceStuffPublicationConsent);
        }
        const userData = await api.getUserData();
        this.firstName = userData.firstName;
        this.lastName = userData.lastName;
        this.email = userData.email;
        this.phoneNumber = this.formatPhone(userData.phoneNumber);
        this.personalAddress = {
            street: '',
            city: '',
            postCode: '',
            country: 'Polska',
        };
        this.companyData = {
            name: '',
            taxNumber: '',
            regon: '',
            street: '',
            city: '',
            postCode: '',
            country: 'Polska',
        };
        this.corporateData = {
            name: '',
            street: '',
            city: '',
            postCode: '',
            deliveryStreet: '',
            deliveryCity: '',
            deliveryPostCode: '',
            krs: '',
            districtCourt: '',
            commercialDivision: '',
            taxNumber: '',
            regon: '',
            country: 'Polska',
        };

        if (userData.businessEntity.type === 'PERSON') {
            this.personalAddress = {
                street: userData.businessEntity.data.address.street || '',
                city: userData.businessEntity.data.address.city || '',
                postCode: this.formatPostCode(userData.businessEntity.data.address.postCode),
                country: userData.businessEntity.data.address.country || 'Polska',
            };
            this.isCompany = false;
            this.isSoleProprietorship = false;
        } else if (userData.businessEntity.type === 'SOLE_PROPRIETORSHIP') {
            this.companyData = {
                name: userData.businessEntity.data.name || '',
                taxNumber: userData.businessEntity.data.taxNumber || '',
                regon: userData.businessEntity.data.regon || '',
                street: userData.businessEntity.data.address.street || '',
                city: userData.businessEntity.data.address.city || '',
                postCode: this.formatPostCode(userData.businessEntity.data.address.postCode),
                country: userData.businessEntity.data.address.country || 'Polska',
            };
            this.isCompany = false;
            this.isSoleProprietorship = true;
        } else {
            this.corporateData = {
                name: userData.businessEntity.data.name || '',
                street: userData.businessEntity.data.address.street || '',
                city: userData.businessEntity.data.address.city || '',
                postCode: this.formatPostCode(userData.businessEntity.data.address.postCode),
                deliveryStreet: userData.businessEntity.data.deliveryAddress.street || '',
                deliveryCity: userData.businessEntity.data.deliveryAddress.city || '',
                deliveryPostCode: this.formatPostCode(userData.businessEntity.data.deliveryAddress.postCode),
                krs: userData.businessEntity.data.krs || '',
                districtCourt: userData.businessEntity.data.districtCourt || '',
                commercialDivision: userData.businessEntity.data.commercialDivision || '',
                taxNumber: userData.businessEntity.data.taxNumber || '',
                regon: userData.businessEntity.data.regon || '',
                country: userData.businessEntity.data.address.country || 'Polska',
            };
            this.isCompany = true;
            this.isSoleProprietorship = false;
        }
        this.receivePayments = userData.receivePayments || false;
        this.publicationConsent = userData.publicationConsent || false;
        this.bankData = {
            bankName: userData.bankData?.bankName || '',
            IBAN: userData.bankData?.IBAN || '',
            chargeClientWithFee: userData.bankData?.chargeClientWithFee || false,
        };
        this.savedState = this.currentState();
    }

    beforeDestroy() {
        if (!this.showActivator) {
            this.$root.$off('openFinanceStuff', this.onOpenFinanceStuff);
            this.$root.$off('openFinanceStuffPublicationConsent', this.onOpenFinanceStuffPublicationConsent);
        }
        if (this.highlightPaymentTimeout) {
            window.clearTimeout(this.highlightPaymentTimeout);
            this.highlightPaymentTimeout = null;
        }
        if (this.highlightPublicationTimeout) {
            window.clearTimeout(this.highlightPublicationTimeout);
            this.highlightPublicationTimeout = null;
        }
        if (this.exitRunBlinkTimeout) {
            window.clearTimeout(this.exitRunBlinkTimeout);
            this.exitRunBlinkTimeout = null;
        }
        if (this.cooldownInterval) {
            window.clearInterval(this.cooldownInterval);
            this.cooldownInterval = null;
        }
        if (this.twoFactorGraceTimer !== null) {
            window.clearTimeout(this.twoFactorGraceTimer);
        }
    }

    get twoFactorMethod() {
        return userModule.user!.twoFactorMethod;
    }

    get publicationConsentSwitchValue() {
        return this.publicationConsent;
    }
    get isTwoFactorEnabled() {
        return !!(userModule.user as any)?.twoFactorEnabled;
    }

    hasRecentTwoFactorValidation() {
        return (userModule.user?.twoFactorValidUntil || 0) > Math.max(this.twoFactorNow, Date.now()) / 1000;
    }

    @Watch('dialogVisible')
    onDialogVisibilityChanged(visible: boolean) {
        if (this.twoFactorGraceTimer !== null) {
            window.clearTimeout(this.twoFactorGraceTimer);
            this.twoFactorGraceTimer = null;
        }
        this.twoFactorNow = Date.now();
        const remaining = (userModule.user?.twoFactorValidUntil || 0) * 1000 - this.twoFactorNow;
        if (visible && remaining > 0) {
            this.twoFactorGraceTimer = window.setTimeout(() => {
                this.twoFactorNow = Date.now();
                this.twoFactorGraceTimer = null;
            }, remaining + 50);
        }
    }

    get canSend2FACodeByEmail() {
        const user = userModule.user as any;
        return !!user?.twoFactorEnabled && user?.twoFactorMethod === 'email';
    }

    get hasUnsavedChanges() {
        return !!this.savedState && JSON.stringify(this.currentState()) !== JSON.stringify(this.savedState);
    }

    get enteredTwoFactorCode() {
        return this.financeTwofaDigits.join('');
    }

    closeDialog() {
        this.restoreSavedState();
        this.dialogVisible = false;
    }

    private currentState(): FinanceStuffState {
        return {
            firstName: this.firstName,
            lastName: this.lastName,
            phoneNumber: this.phoneNumber,
            email: this.email,
            receivePayments: this.receivePayments,
            publicationConsent: this.publicationConsent,
            isSoleProprietorship: this.isSoleProprietorship,
            isCompany: this.isCompany,
            manualTransfers: this.manualTransfers,
            przelewy24: this.przelewy24,
            paypal: this.paypal,
            personalAddress: { ...this.personalAddress },
            companyData: { ...this.companyData },
            corporateData: { ...this.corporateData },
            bankData: { ...this.bankData },
        };
    }

    private restoreSavedState() {
        if (!this.savedState) return;

        const state = this.savedState;
        this.firstName = state.firstName;
        this.lastName = state.lastName;
        this.phoneNumber = state.phoneNumber;
        this.email = state.email;
        this.receivePayments = state.receivePayments;
        this.publicationConsent = state.publicationConsent;
        this.isSoleProprietorship = state.isSoleProprietorship;
        this.isCompany = state.isCompany;
        this.manualTransfers = state.manualTransfers;
        this.przelewy24 = state.przelewy24;
        this.paypal = state.paypal;
        this.personalAddress = { ...state.personalAddress };
        this.companyData = { ...state.companyData };
        this.corporateData = { ...state.corporateData };
        this.bankData = { ...state.bankData };
        this.resetTwoFactorEntry();
        (this.$refs.financeForm as any)?.resetValidation?.();
    }

    private resetTwoFactorEntry() {
        this.financeTwofaDigits = ['', '', '', '', '', ''];
        this.twoFactorCodeErrors = [];
        this.isCooldownActive = false;
        this.cooldownTime = 0;
        if (this.cooldownInterval) {
            window.clearInterval(this.cooldownInterval);
            this.cooldownInterval = null;
        }
    }

    public async sendEmailCode() {
        if (this.isCooldownActive || !this.hasUnsavedChanges) return;
        
        await api.sendEmail2FACode();
        
        this.isCooldownActive = true;
        this.cooldownTime = 30;
        
        notifier.success({
            title: 'Wysłano',
            body: 'Kod wysłano mailem',
        });
        
        this.cooldownInterval = window.setInterval(() => {
            this.cooldownTime--;
            if (this.cooldownTime <= 0) {
                this.isCooldownActive = false;
                if (this.cooldownInterval) {
                    window.clearInterval(this.cooldownInterval);
                    this.cooldownInterval = null;
                }
            }
        }, 1000);
    }

    public onFinanceTwofaDigitInput(index: number, event: Event) {
        if (!this.hasUnsavedChanges) return;
        const target = event.target as HTMLInputElement;
        const nextDigit = (target.value || '').replace(/\D/g, '').slice(-1);
        this.$set(this.financeTwofaDigits, index, nextDigit);
        this.twoFactorCodeErrors = [];

        if (nextDigit && index < this.financeTwofaDigits.length - 1) {
            this.focusFinanceTwofaDigit(index + 1);
        }
    }

    public onFinanceTwofaDigitKeydown(index: number, event: KeyboardEvent) {
        const target = event.target as HTMLInputElement;

        if (event.key === 'Backspace' && !target.value && index > 0) {
            this.focusFinanceTwofaDigit(index - 1);
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            this.focusFinanceTwofaDigit(index - 1);
            return;
        }

        if (event.key === 'ArrowRight' && index < this.financeTwofaDigits.length - 1) {
            event.preventDefault();
            this.focusFinanceTwofaDigit(index + 1);
        }
    }

    public onFinanceTwofaPaste(event: ClipboardEvent) {
        if (!this.hasUnsavedChanges) return;
        const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
        if (!pasted) {
            return;
        }

        this.financeTwofaDigits = ['', '', '', '', '', ''];
        pasted.split('').forEach((digit, index) => {
            this.$set(this.financeTwofaDigits, index, digit);
        });
        this.twoFactorCodeErrors = [];

        const focusIndex = Math.min(pasted.length, this.financeTwofaDigits.length - 1);
        this.focusFinanceTwofaDigit(focusIndex);
    }

    private focusFinanceTwofaDigit(index: number) {
        this.$nextTick(() => {
            const inputRef = this.$refs[`financeTwofaDigit${index}`] as HTMLInputElement[] | HTMLInputElement | undefined;
            const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
            input?.focus?.();
            input?.select?.();
        });
    }

    public onDialogOutsideClick() {
        this.flashExitRun();
    }

    public onOpenFinanceStuff(payload?: { highlight?: boolean; target?: 'paymentReceiving' | 'publicationConsent' }) {
        this.openDialog(!!payload?.highlight, payload?.target);
    }

    public onOpenFinanceStuffPublicationConsent() {
        this.openDialog(true, 'publicationConsent');
    }

    public openDialog(highlight = false, target?: 'paymentReceiving' | 'publicationConsent') {
        this.resetTwoFactorEntry();
        this.dialogVisible = true;
        if (highlight) {
            this.$nextTick(() => {
                // The business fields expand for 300 ms when the dialog opens.
                window.setTimeout(() => {
                    if (!this.dialogVisible) return;
                    const scroller = this.$refs.financeScrollContainer as HTMLElement | undefined;
                    if (!scroller) return;
                    scroller.scrollTop = scroller.scrollHeight;
                    window.requestAnimationFrame(() => {
                        if (target === 'publicationConsent') this.flashPublicationConsent();
                        else this.flashPaymentReceiving();
                    });
                }, 350);
            });
        }
    }

    public flashPaymentReceiving() {
        this.highlightPaymentReceiving = false;
        if (this.highlightPaymentTimeout) {
            window.clearTimeout(this.highlightPaymentTimeout);
            this.highlightPaymentTimeout = null;
        }

        this.$nextTick(() => {
            this.highlightPaymentReceiving = true;
            this.highlightPaymentTimeout = window.setTimeout(() => {
                this.highlightPaymentReceiving = false;
                this.highlightPaymentTimeout = null;
            }, 1800);
        });
    }

    public flashPublicationConsent() {
        this.highlightPaymentReceiving = false;
        if (this.highlightPaymentTimeout) {
            window.clearTimeout(this.highlightPaymentTimeout);
            this.highlightPaymentTimeout = null;
        }
        this.highlightPublicationConsent = false;
        if (this.highlightPublicationTimeout) {
            window.clearTimeout(this.highlightPublicationTimeout);
            this.highlightPublicationTimeout = null;
        }

        this.$nextTick(() => {
            this.highlightPublicationConsent = true;
            this.highlightPublicationTimeout = window.setTimeout(() => {
                this.highlightPublicationConsent = false;
                this.highlightPublicationTimeout = null;
            }, 1800);
        });
    }

    private flashExitRun() {
        this.exitRunBlink = false;
        if (this.exitRunBlinkTimeout) {
            window.clearTimeout(this.exitRunBlinkTimeout);
            this.exitRunBlinkTimeout = null;
        }

        this.$nextTick(() => {
            this.exitRunBlink = true;
            this.exitRunBlinkTimeout = window.setTimeout(() => {
                this.exitRunBlink = false;
                this.exitRunBlinkTimeout = null;
            }, 900);
        });
    }

    onManualTransfersChange(value: boolean) {
        if (!value && this.isOnlyEnabledPaymentMethod('manual')) {
            // Prevent disabling if it's the only enabled method
            this.$nextTick(() => {
                this.manualTransfers = true;
            });
        } else if (!value) {
            this.$nextTick(() => (this.$refs.financeForm as any)?.validate());
        }
    }

    onPrzelewy24Change(value: boolean) {
        if (!value && this.isOnlyEnabledPaymentMethod('przelewy24')) {
            // Prevent disabling if it's the only enabled method
            this.$nextTick(() => {
                this.przelewy24 = true;
            });
        }
    }

    onPaypalChange(value: boolean) {
        if (!value && this.isOnlyEnabledPaymentMethod('paypal')) {
            // Prevent disabling if it's the only enabled method
            this.$nextTick(() => {
                this.paypal = true;
            });
        }
    }

    isOnlyEnabledPaymentMethod(method: string): boolean {
        const enabledMethods = [
            this.manualTransfers,
            this.przelewy24,
            this.paypal
        ].filter(Boolean);
        
        return enabledMethods.length === 1 && this.getMethodValue(method);
    }

    getMethodValue(method: string): boolean {
        switch (method) {
            case 'manual':
                return this.manualTransfers;
            case 'przelewy24':
                return this.przelewy24;
            case 'paypal':
                return this.paypal;
            default:
                return false;
        }
    }

    ensureAtLeastOnePaymentMethodEnabled() {
        if (!this.manualTransfers && !this.przelewy24 && !this.paypal) {
            // If no payment method is enabled, enable manual transfers by default
            this.manualTransfers = true;
        }
    }

    onPublicationConsentChange(value: boolean) {
        this.publicationConsent = value;
    }

    private buildBusinessEntityPayload(): BusinessEntity {
        if (this.isCompany) {
            return {
                type: 'COMPANY',
                data: {
                    name: this.corporateData.name,
                    taxNumber: this.corporateData.taxNumber,
                    regon: this.corporateData.regon,
                    krs: this.corporateData.krs,
                    districtCourt: this.corporateData.districtCourt,
                    commercialDivision: this.corporateData.commercialDivision,
                    address: {
                        street: this.corporateData.street,
                        city: this.corporateData.city,
                        postCode: this.corporateData.postCode,
                        country: this.corporateData.country,
                    },
                    deliveryAddress: {
                        street: this.corporateData.deliveryStreet,
                        city: this.corporateData.deliveryCity,
                        postCode: this.corporateData.deliveryPostCode,
                        country: this.corporateData.country,
                    },
                },
            };
        }

        if (this.isSoleProprietorship) {
            return {
                type: 'SOLE_PROPRIETORSHIP',
                data: {
                    name: this.companyData.name,
                    taxNumber: this.companyData.taxNumber,
                    regon: this.companyData.regon,
                    address: {
                        street: this.companyData.street,
                        city: this.companyData.city,
                        postCode: this.companyData.postCode,
                        country: this.companyData.country,
                    },
                    deliveryAddress: {
                        street: this.companyData.street,
                        city: this.companyData.city,
                        postCode: this.companyData.postCode,
                        country: this.companyData.country,
                    },
                },
            };
        }

        return {
            type: 'PERSON',
            data: {
                address: {
                    street: this.personalAddress.street,
                    city: this.personalAddress.city,
                    postCode: this.personalAddress.postCode,
                    country: this.personalAddress.country,
                },
            },
        };
    }

    private scrollToFirstFormError() {
        this.$nextTick(() => {
            const container = this.$refs.financeScrollContainer as HTMLElement | undefined;
            const errorElement = container?.querySelector('.v-input.error--text, .error--text') as HTMLElement | null;

            if (container && errorElement) {
                this.$vuetify.goTo(errorElement, { container });
            }
        });
    }

    async submit() {
        const form = this.$refs.financeForm as Vue & { validate?: (w: boolean) => boolean };

        if (!this.hasUnsavedChanges) {
            return;
        }

        if (!this.isTwoFactorEnabled || !form.validate?.(true)) {
            this.scrollToFirstFormError();
            this.flashExitRun();
            return;
        }
        const receivePayments = this.receivePayments;
        const businessEntity = this.buildBusinessEntityPayload();
        const bankData = (this.receivePayments && !!this.bankData.bankName) ? { ...this.bankData } : undefined;
        const response = await api.updateAccountData({
            firstName: this.firstName,
            lastName: this.lastName,
            phoneNumber: this.phoneNumber,
            email: this.email,
            publicationConsent: this.publicationConsent,
            receivePayments: this.receivePayments,
            businessEntity,
            ...(bankData && { bankData }),
            twoFactorCode: this.hasRecentTwoFactorValidation() ? '' : this.enteredTwoFactorCode,
        });
        if (response && response.error === 'invalid_2fa') {
            userModule.updateUser({ twoFactorValidUntil: 0 });
            notifier.error({
                title: 'Niepoprawny kod',
                body: 'Podany kod autoryzacyjny jest niepoprawny. Upewnij się, że wpisałeś poprawny kod 2FA.',
            });
            this.twoFactorCodeErrors.push('Niepoprawny kod 2FA');
        } else {
            if (response?.twoFactorValidUntil) {
                userModule.updateUser({ twoFactorValidUntil: response.twoFactorValidUntil });
            }
            userModule.updateUser({
                publicationConsent: this.publicationConsent,
                paymentReceiving: this.receivePayments,
            });
            this.savedState = this.currentState();
            notifier.success({
                title: 'Zapisano',
                body: 'Zmiany zostały zapisane',
            });
            this.dialogVisible = false;
            // Update the store from the payload we just persisted instead of re-fetching it.
            userModule.setAccountData({
                firstName: this.firstName,
                lastName: this.lastName,
                phoneNumber: this.phoneNumber,
                email: this.email,
                publicationConsent: this.publicationConsent,
                receivePayments,
                businessEntity,
                bankData,
            });
        }
    }
}
